// ✅ src/pages/VehicleV2/VehicleTyreSupplierDetailReport.jsx
// UPDATED FRONTEND
// - Uses localStorage session headers
// - Sends x-finyearid / x-finyear / x-branchid / x-branchtype / x-userid
// - Keeps same UI, filters, export, print, row print, pagination, autocomplete

import React, { useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import axios from "axios";
import {
  FaSearch,
  FaPrint,
  FaFilter,
  FaArrowLeft,
  FaFileExport,
  FaInfoCircle,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "http://nissi-india.com/api/supplierDetailReport";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();

const toNum = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
};

const pick = (row, keys, def = "") => {
  for (const k of keys) {
    const v = row?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return def;
};

const exportToCsv = (rows, filename = "vehicle-tyre-supplier-detail.csv") => {
  if (!rows?.length) return;

  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(","),
    ...rows.map((r) =>
      headers
        .map((h) => {
          const cell = r[h] ?? "";
          const s = String(cell).replace(/"/g, '""');
          return `"${s}"`;
        })
        .join(",")
    ),
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const isFutureDate = (yyyyMmDd) => {
  if (!yyyyMmDd) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const d = new Date(yyyyMmDd);
  d.setHours(0, 0, 0, 0);

  return d > today;
};

const safeSum = (rows, keyCandidates) => {
  let t = 0;
  for (const r of rows || []) {
    const raw = pick(r, keyCandidates, "0");
    const n = Number(raw);
    if (Number.isFinite(n)) t += n;
  }
  return Math.round((t + Number.EPSILON) * 100) / 100;
};

export default function VehicleTyreSupplierDetailReport() {
  const navigate = useNavigate();
  const { isDark, skin, setSkin, theme } = useContext(ThemeContext);

  const currentSkin = useMemo(() => getCurrentSkin(theme || skin), [theme, skin]);
  const { baseColor, baseShade } = currentSkin || {};
  const buttonClass = `bg-${baseColor || "blue"}-${baseShade || "600"}`;
  const hoverClass = `hover:bg-${baseColor || "blue"}-700`;

  /* ---------------- localStorage session values ---------------- */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const sessionBranchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("branchid") ||
    "";
  const sessionBranchType = (
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO"
  ).toUpperCase();
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginUserId") ||
    localStorage.getItem("loginuserid") ||
    "";

  const apiHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": sessionBranchId,
      "x-branchtype": sessionBranchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, sessionBranchId, sessionBranchType, userId]
  );

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  const [meta, setMeta] = useState({
    suppliers: [],
  });

  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    supplierName: "Select",
    tyreSerialNo: "",
    branchId:
      sessionBranchType === "HO"
        ? sessionBranchId || ""
        : sessionBranchId || "",
  });

  const [rows, setRows] = useState([]);

  const [ac, setAc] = useState({
    tyreSerialNo: [],
  });

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  /* ---------------- validation ---------------- */
  const validateDates = useCallback((f) => {
    if (f.fromDate && isFutureDate(f.fromDate)) {
      toast.error("Select from date lesser than current date");
      return false;
    }

    if (f.toDate && isFutureDate(f.toDate)) {
      toast.error("Select to date lesser than current date");
      return false;
    }

    if (f.fromDate && f.toDate) {
      const a = new Date(f.fromDate).getTime();
      const b = new Date(f.toDate).getTime();
      if (b < a) {
        toast.error("Please select from date less than to date");
        return false;
      }
    }

    return true;
  }, []);

  /* ---------------- APIs ---------------- */
  const loadMeta = useCallback(async () => {
    try {
      const rs = await axios.get(`${API_BASE}/meta`, {
        headers: apiHeaders,
        params: {
          branchId: sessionBranchId || "",
          branchType: sessionBranchType || "HO",
          loginUserId: userId || "",
        },
      });

      if (!rs.data?.success) {
        throw new Error(rs.data?.message || "Meta load failed");
      }

      setMeta({
        suppliers: Array.isArray(rs.data.suppliers) ? rs.data.suppliers : [],
      });
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Failed to load dropdowns");
      setMeta({ suppliers: [] });
    }
  }, [apiHeaders, sessionBranchId, sessionBranchType, userId]);

  const loadList = useCallback(
    async (f, showToast = true) => {
      try {
        if (!validateDates(f)) {
          setRows([]);
          return;
        }

        setLoading(true);

        const branchToSend =
          sessionBranchType === "HO"
            ? toStr(f.branchId || sessionBranchId || "")
            : toStr(sessionBranchId || "");

        const params = {
          branchId: branchToSend,
          branchType: sessionBranchType || "HO",
          loginUserId: userId || "",
          fromDate: toStr(f.fromDate),
          toDate: toStr(f.toDate),
          supplierName: toStr(f.supplierName || "Select"),
          tyreSerialNo: toStr(f.tyreSerialNo),
        };

        const rs = await axios.get(`${API_BASE}/list`, {
          headers: apiHeaders,
          params,
        });

        if (!rs.data?.success) {
          throw new Error(rs.data?.message || "List load failed");
        }

        const list = Array.isArray(rs.data.rows) ? rs.data.rows : [];
        setRows(list);
        setCurrentPage(1);

        if (showToast) {
          toast.success(`Found ${rs.data.count ?? list.length} records`);
        }
      } catch (e) {
        toast.error(e?.response?.data?.message || e.message || "Failed to load report");
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [apiHeaders, sessionBranchId, sessionBranchType, userId, validateDates]
  );

  /* ---------------- init ---------------- */
  useEffect(() => {
    let mounted = true;

    const init = async () => {
      try {
        setPageLoading(true);
        await loadMeta();

        if (!mounted) return;

        const initialFilters = {
          fromDate: "",
          toDate: "",
          supplierName: "Select",
          tyreSerialNo: "",
          branchId: sessionBranchId || "",
        };

        setFilters(initialFilters);
        await loadList(initialFilters, false);
      } finally {
        if (mounted) setPageLoading(false);
      }
    };

    init();
    return () => {
      mounted = false;
    };
  }, [loadMeta, loadList, sessionBranchId]);

  /* ---------------- autocomplete ---------------- */
  const debounceRef = useRef({});

  const debounce = (key, fn, delay = 250) => {
    clearTimeout(debounceRef.current[key]);
    debounceRef.current[key] = setTimeout(fn, delay);
  };

  useEffect(() => {
    const prefix = toStr(filters.tyreSerialNo);
    if (prefix.length < 1) {
      setAc((p) => ({ ...p, tyreSerialNo: [] }));
      return;
    }

    debounce("tyreSerialNo", async () => {
      try {
        const branchToSend =
          sessionBranchType === "HO"
            ? toStr(filters.branchId || sessionBranchId || "")
            : toStr(sessionBranchId || "");

        const rs = await axios.get(`${API_BASE}/ac/tyreSerialNo`, {
          headers: apiHeaders,
          params: {
            branchId: branchToSend,
            branchType: sessionBranchType || "HO",
            supplierName: toStr(filters.supplierName || "Select"),
            prefix,
          },
        });

        const items = rs.data?.items || rs.data || [];
        setAc((p) => ({
          ...p,
          tyreSerialNo: Array.isArray(items) ? items : [],
        }));
      } catch {
        setAc((p) => ({ ...p, tyreSerialNo: [] }));
      }
    });
  }, [
    apiHeaders,
    filters.tyreSerialNo,
    filters.supplierName,
    filters.branchId,
    sessionBranchId,
    sessionBranchType,
  ]);

  /* ---------------- handlers ---------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((p) => ({ ...p, [name]: value }));
  };

  const handleClearFilters = () => {
    setFilters((p) => ({
      ...p,
      fromDate: "",
      toDate: "",
      supplierName: "Select",
      tyreSerialNo: "",
    }));
    toast.info("Filters cleared");
  };

  const handleSearch = () => loadList(filters);

  const handleViewAll = () => {
    const next = {
      ...filters,
      fromDate: "",
      toDate: "",
      supplierName: "Select",
      tyreSerialNo: "",
    };
    setFilters(next);
    loadList(next);
  };

  const handleExport = () => {
    if (!rows.length) {
      toast.info("No rows to export");
      return;
    }
    exportToCsv(rows, "vehicle-tyre-supplier-detail.csv");
    toast.success("Exported CSV");
  };

  const handlePrint = () => {
    if (!rows.length) {
      toast.info("No rows to print");
      return;
    }

    const suppliername =
      filters.supplierName !== "Select" ? filters.supplierName : "%";
    const tyreserialno = toStr(filters.tyreSerialNo) ? toStr(filters.tyreSerialNo) : "%";

    const qs = new URLSearchParams({
      suppliername,
      tyreserialno,
      fromDate: toStr(filters.fromDate),
      toDate: toStr(filters.toDate),
      branchId: String(toStr(filters.branchId || sessionBranchId || "")),
      branchType: toStr(sessionBranchType),
      loginUserId: String(toStr(userId)),
    }).toString();

    window.open(`/vehicle/VehicleTyreSupplierDetailReport_Print?${qs}`, "_blank");
  };

  const handleRowPrint = (tyreId) => {
    if (!tyreId) return;

    const qs = new URLSearchParams({
      tyreId: String(tyreId),
      branchId: String(toStr(filters.branchId || sessionBranchId || "")),
      branchType: toStr(sessionBranchType),
      loginUserId: String(toStr(userId)),
    }).toString();

    window.open(`/vehicle/VehicleTyreSupplierDetailReport_Print?${qs}`, "_blank");
  };

  /* ---------------- pagination ---------------- */
  const totalPages = Math.ceil(rows.length / itemsPerPage) || 1;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  const currentItems = useMemo(
    () => rows.slice(indexOfFirstItem, indexOfLastItem),
    [rows, indexOfFirstItem, indexOfLastItem]
  );

  const paginate = (p) => setCurrentPage(p);

  /* ---------------- totals ---------------- */
  const grandTotalRate = useMemo(
    () => safeSum(rows, ["fld_TyreAmounts", "Rate", "rate"]),
    [rows]
  );

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VEHICLE TYRE SUPPLIER DETAIL REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-8 flex-wrap gap-3">
            <div className="flex items-center gap-4 flex-wrap">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Vehicle Tyre Supplier Detail Report
              </h1>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {rows.length} Records
              </span>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-emerald-900 text-emerald-100" : "bg-emerald-100 text-emerald-800"
                }`}
              >
                Total Rate: {grandTotalRate}
              </span>

              {(loading || pageLoading) && (
                <span className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Loading...
                </span>
              )}
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800" + (showFilters ? " bg-gray-800" : "")
                    : "border-gray-300 text-gray-700 hover:bg-gray-50" + (showFilters ? " bg-gray-50" : "")
                }`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleExport}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaFileExport className="w-4 h-4" />
                Export
              </button>

              <button
                onClick={() => navigate(-1)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {showFilters && (
            <div
              className={`rounded-xl p-6 mb-6 border ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
              }`}
            >
              <h3
                className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                <FaFilter /> FILTRATION AREA
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                <div>
                  <label className={labelClass}>From Date</label>
                  <input
                    type="date"
                    name="fromDate"
                    value={filters.fromDate}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>To Date</label>
                  <input
                    type="date"
                    name="toDate"
                    value={filters.toDate}
                    onChange={handleChange}
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Supplier Name</label>
                  <select
                    name="supplierName"
                    value={filters.supplierName}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="Select">Select</option>
                    {meta.suppliers.map((s) => (
                      <option key={s.value ?? s.label} value={s.value ?? s.label}>
                        {s.label ?? s.value}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Serial Number</label>
                  <input
                    name="tyreSerialNo"
                    value={filters.tyreSerialNo}
                    onChange={handleChange}
                    className={inputClass}
                    list="ac_tyreSerialNo"
                    placeholder="Type to search..."
                  />
                  <datalist id="ac_tyreSerialNo">
                    {ac.tyreSerialNo.map((x, i) => (
                      <option key={`${x}-${i}`} value={x} />
                    ))}
                  </datalist>
                </div>
              </div>

              <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700 flex-wrap gap-3">
                <button
                  onClick={handleClearFilters}
                  className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium"
                >
                  Clear All Filters
                </button>

                <div className="flex gap-4 flex-wrap justify-end">
                  <button
                    onClick={handleViewAll}
                    className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold"
                  >
                    View All
                  </button>

                  <button
                    onClick={handleSearch}
                    className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold`}
                    disabled={loading || pageLoading}
                  >
                    <FaSearch />
                    {loading ? "Searching..." : "Search Records"}
                  </button>

                  <button
                    type="button"
                    onClick={handlePrint}
                    className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold`}
                    disabled={loading || pageLoading}
                  >
                    <FaPrint />
                    Print
                  </button>
                </div>
              </div>
            </div>
          )}

          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {rows.length === 0 ? (
              <div className="text-center py-16">
                <div
                  className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                    isDark ? "bg-gray-700" : "bg-gray-100"
                  }`}
                >
                  <FaInfoCircle className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className={`text-xl font-semibold mb-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                  {pageLoading ? "Loading..." : "No Records Found"}
                </h3>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-4 text-center font-semibold whitespace-nowrap">S.No</th>
                      <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">Supplier Name</th>
                      <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">Tyre Serial Number</th>
                      <th className="px-4 py-4 text-left font-semibold whitespace-nowrap">Purchase Date</th>
                      <th className="px-4 py-4 text-right font-semibold whitespace-nowrap">Rate</th>
                      <th className="px-4 py-4 text-center font-semibold whitespace-nowrap">Print</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {currentItems.map((r, idx) => {
                      const tyreId = pick(r, ["fld_NewTyre_ID", "tyreId", "TyreId"]);
                      const supplier = pick(r, ["fld_Purchasefrom", "SupplierName"]);
                      const serial = pick(r, ["fld_Tyreserialno", "TyreSerialNo"]);
                      const purchasedate = pick(r, ["fld_Purchasedate", "PurchaseDate"]);
                      const rate = pick(r, ["fld_TyreAmounts", "Rate"]);

                      return (
                        <tr
                          key={`${tyreId}-${idx}`}
                          className={`${isDark ? "bg-gray-800 hover:bg-gray-750" : "bg-white hover:bg-gray-50"}`}
                        >
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {indexOfFirstItem + idx + 1}
                          </td>
                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {supplier || "-"}
                          </td>
                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {serial || "-"}
                          </td>
                          <td className={`px-4 py-4 text-sm whitespace-nowrap ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {purchasedate || "-"}
                          </td>
                          <td className={`px-4 py-4 text-sm text-right font-medium ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                            {rate || "0"}
                          </td>
                          <td className="px-4 py-4 text-center">
                            <button
                              type="button"
                              onClick={() => handleRowPrint(tyreId)}
                              className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-white ${buttonClass} ${hoverClass}`}
                              title="Print this tyre record"
                            >
                              <FaPrint />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>

                  <tfoot>
                    <tr className={`${isDark ? "bg-gray-900/40" : "bg-gray-50"}`}>
                      <td className={`px-4 py-4 text-sm font-semibold text-center ${isDark ? "text-gray-200" : "text-gray-900"}`} />
                      <td className={`px-4 py-4 text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-900"}`} />
                      <td className={`px-4 py-4 text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-900"}`} />
                      <td className={`px-4 py-4 text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                        Total
                      </td>
                      <td className={`px-4 py-4 text-sm font-semibold text-right ${isDark ? "text-gray-200" : "text-gray-900"}`}>
                        {grandTotalRate}
                      </td>
                      <td className="px-4 py-4" />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {rows.length > 0 && (
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center">
              <div className={`text-sm mb-4 sm:mb-0 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, rows.length)} of {rows.length} entries
              </div>

              <nav className="flex gap-1">
                <button
                  onClick={() => paginate(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 border rounded text-sm ${
                    currentPage === 1
                      ? isDark
                        ? "border-gray-700 text-gray-500 cursor-not-allowed"
                        : "border-gray-300 text-gray-400 cursor-not-allowed"
                      : isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Previous
                </button>

                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) pageNum = i + 1;
                  else if (currentPage <= 3) pageNum = i + 1;
                  else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                  else pageNum = currentPage - 2 + i;

                  return (
                    <button
                      key={pageNum}
                      onClick={() => paginate(pageNum)}
                      className={`px-3 py-1 border rounded text-sm ${
                        currentPage === pageNum
                          ? `${buttonClass} text-white`
                          : isDark
                          ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                          : "border-gray-300 text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1 border rounded text-sm ${
                    currentPage === totalPages
                      ? isDark
                        ? "border-gray-700 text-gray-500 cursor-not-allowed"
                        : "border-gray-300 text-gray-400 cursor-not-allowed"
                      : isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Next
                </button>
              </nav>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}